import { getLogger } from '@packages/core/logging';

import type { TechnicianAssignmentEntity } from '../entities';

import { StsBackendOperation } from '../../../sts-backend-operation';
import { toTechnicianAssignmentEntity } from '../helpers';

const logger = getLogger('techline.assignments');

/**
 * Parameters for refreshing technician assignments for a case. Supports a fallback list so the
 * caller can preserve previously-known assignments when the API returns an empty result set.
 */
export type RefreshAssignmentsParams = {
    /** The case number to fetch assignments for. */
    caseNumber: string;

    /** Assignments to fall back to when the API returns an empty result set. */
    fallbackAssignments?: TechnicianAssignmentEntity[];
};

/**
 * Fetch the current technician assignments for a techline case. When the API returns an empty
 * result set, falls back to a caller-supplied list (typically the assignments already known from
 * a previous fetch or from the case detail response).
 */
export class RefreshAssignments extends StsBackendOperation {
    /**
     * Fetch assignments for a case, mapping them to domain entities. Falls back to the provided
     * list when the response is empty.
     *
     * @param params - the case number and optional fallback assignments
     * @returns the fetched assignments as domain entities, or the fallback list if the response was empty
     */
    async exec(params: RefreshAssignmentsParams): Promise<TechnicianAssignmentEntity[]> {
        try {
            const { caseNumber, fallbackAssignments } = params;

            const response = await this.client.getCaseTechnicianAssignments({ caseNumber });

            // Map API results to domain entities when the response is successful and non-empty
            if (response.success && response.data?.results && response.data.results.length > 0) {
                return response.data.results.map(toTechnicianAssignmentEntity);
            }

            return fallbackAssignments ?? [];
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to get case technician assignments';
            logger.error('Failed to refresh the case technician assignments', { error: e });
            throw new Error(message);
        }
    }
}
